import actionTypes from '../common/actionTypes';
import policyAPI from './policyAPI';

const policyActions = {
	setDevQuestionnaire: () => {
		return { type: actionTypes.SET_DEV_QUESTIONNAIRE };
	},
	resetCustomerPolicy: () => {
		return { type: actionTypes.RESET_CUSTOMER_POLICY };
	},
	resetCustomerPolicyBeneficiary: () => {
		return (dispatch: any) => {
			dispatch({ type: actionTypes.RESET_CUSTOMER_POLICY_BENEFICIARY });
			return Promise.resolve();
		};
	},
	resetCustomerPolicyAnswer: () => {
		return { type: actionTypes.RESET_POLICY_ANSWER };
	},
	setCustomerPolicy: (customerPolicy: CustomerPolicy) => {
		return { type: actionTypes.SET_CUSTOMER_POLICY, customerPolicy };
	},
	setCustomerPolicies: (customerPolicy: CustomerPolicy) => {
		return { type: actionTypes.SET_CUSTOMER_POLICIES, customerPolicy };
	},

	setCustomerPolicyBeneficiary: (customerPolicyBeneficiary: CustomerPolicyBeneficiaryDto) => {
		return { type: actionTypes.SET_CUSTOMER_POLICY_BENEFICIARY, customerPolicyBeneficiary };
	},
	setTrustee: (trustee: CustomerPolicyBeneficiaryDto) => {
		return { type: actionTypes.SET_TRUSTEE, trustee }; 
	},
	setCustomerPolicyBeneficiaries: (beneficiaries: CustomerPolicyBeneficiaryDto[]) => {
		return (dispatch: any) => {
			dispatch({ type: actionTypes.SET_CUSTOMER_POLICY_BENEFICIARIES, beneficiaries });
			Promise.resolve();
		};
	},

	setQuestionnaire: (questionnaire: any) => {
		return { type: actionTypes.SET_QUESTIONNAIRE, questionnaire };
	},
	resetQuestionnaire: () => {
		return { type: actionTypes.RESET_QUESTIONNAIRE };
	},
	setPremiumChanged: (premiumChanged: boolean) => {
		return { type: actionTypes.SET_PREMIUM_CHANGED, premiumChanged };
	},
	fetchCustomerPolicyDocumentUrl: (userId: string, policyId: string) => (dispatch: any) => {
		return policyAPI.fetchCustomerPolicyDocumentUrl(userId, policyId)
			.then(url => {
				dispatch({ type: actionTypes.FETCH_CUSTOMER_POLICY_DOCUMENT_URL, url });
			});
	},
	fetchCustomerPolicy: (userId: string, policyId: string) => (dispatch: any) => {
		return policyAPI.fetchCustomerPolicy(userId, policyId)
			.then(customerPolicy => {
				dispatch({ type: actionTypes.FETCH_CUSTOMER_POLICY, customerPolicy });
			})
			.catch(err => {
				if (err.responseStatus) {
					toastr.error(err.responseStatus.message);
				}
			});
	},
	fetchCustomerPolicyFreeTrialMonths: (userId: string, policyId: string) => (dispatch: any) => {
		return policyAPI.fetchCustomerPolicyFreeTrialMonths(userId, policyId)
			.then(months => {
				dispatch({ type: actionTypes.FETCH_CUSTOMER_POLICY_FREE_TRIAL_MONTHS, months });
			})
			.catch(err => {
				if (err.responseStatus) {
					toastr.error(err.responseStatus.message);
				}
			});
	},
	fetchCustomerPolicies: (userId: string, includeInactive?: boolean) => (dispatch: any) => {
		return policyAPI.fetchCustomerPolicies(userId, includeInactive)
			.then(policies => {
				dispatch({ type: actionTypes.FETCH_CUSTOMER_POLICIES, policies });
			})
			.catch(err => {
				if (err.responseStatus) {
					toastr.error(err.responseStatus.message);
				}
			});
	},
	fetchCustomerPolicyBeneficiary: (userId: string, beneficiaryId: string) => (dispatch: any) => {
		return policyAPI.fetchCustomerPolicyBeneficiary(userId, beneficiaryId)
			.then((customerPolicyBeneficiary: CustomerPolicyBeneficiaryDto) => {
				dispatch({ type: actionTypes.FETCH_CUSTOMER_POLICY_BENEFICIARY, customerPolicyBeneficiary });
			})
			.catch(err => {
				throw err;
			});
	},
	fetchCustomerPolicyBeneficiaries: (userId: string) => (dispatch: any) => {
		return policyAPI.fetchCustomerPolicyBeneficiaries(userId)
			.then((beneficiaries: CustomerPolicyBeneficiariesDto) => {
				dispatch({ type: actionTypes.FETCH_CUSTOMER_POLICY_BENEFICIARIES, beneficiaries });
			})
			.catch(err => {
				throw err;
			});
	},
	fetchCustomerPolicyBeneficiaryForm: (userId: string, policyId: string, formId: string) => (dispatch: any) => {
		return policyAPI.fetchCustomerPolicyBeneficiaryForm(userId, policyId, formId)
			.then(() => {
				dispatch({ type: actionTypes.FETCH_CUSTOMER_POLICY_BENEFICIARY_FORM });
			})
			.catch(err => {
				throw err;
			});
	},

	fetchPlanOptions: (workflow: string) => (dispatch: any) => {
		return policyAPI.fetchPlanOptions(workflow)
			.then((planOptions: CustomerPolicy[]) => {
				dispatch({ type: actionTypes.FETCH_PLAN_OPTIONS, planOptions });
			})
			.catch(err => {
				throw err;
			});
	},
	fetchSimplePlanOptions: (params: GetSimplePoliciesRequest) => (dispatch: any) => {
		return policyAPI.fetchSimplePlanOptions(params)
			.then((planOptions: CustomerPolicy[]) => {
				dispatch({ type: actionTypes.FETCH_PLAN_OPTIONS, planOptions });
			})
			.catch(err => {
				throw err;
			});
	},
	fetchAdvisoryPlanOptions: (params: GetAdvisoryPoliciesRequest) => (dispatch: any) => {
		return policyAPI.fetchAdvisoryPlanOptions(params)
			.then((planOptions: CustomerPolicy[]) => {
				dispatch({ type: actionTypes.FETCH_PLAN_OPTIONS, planOptions });
			})
			.catch(err => {
				throw err;
			});
	},
	fetchBespokePlanOptions: (params: GetBespokePoliciesRequest) => (dispatch: any) => {
		return policyAPI.fetchBespokePlanOptions(params)
			.then((planOptions: CustomerPolicy[]) => {
				dispatch({ type: actionTypes.FETCH_PLAN_OPTIONS, planOptions });
			})
			.catch(err => {
				throw err;
			});
	},
	fetchRatePlusMinus: (policyId: string) => (dispatch: any) => {
		return policyAPI.fetchRatePlusMinus(policyId)
			.then((ratePlusMinus: number) => {
				dispatch({ type: actionTypes.FETCH_RATE_PLUS_MINUS_SUCCESS, ratePlusMinus });
			})
			.catch(err => {
				throw err;
			});
	},
	fetchIsHardDeclined: (userId: string, policyId: string) => (dispatch: any) => {
		return policyAPI.fetchIsHardDeclined(userId, policyId)
			.then((isHardDeclined: DeclineCheckerDto) => {
				dispatch({ type: actionTypes.FETCH_IS_HARD_DECLINED_SUCCESS, isHardDeclined });
			})
			.catch(err => {
				throw err;
			});
	},
	fetchIsSoftDeclined: (userId: string, policyId: string) => (dispatch: any) => {
		return policyAPI.fetchIsSoftDeclined(userId, policyId)
			.then((isSoftDeclined: DeclineCheckerDto) => {
				dispatch({ type: actionTypes.FETCH_IS_SOFT_DECLINED_SUCCESS, isSoftDeclined });
			})
			.catch(err => {
				throw err;
			});
	},
	fetchQuestionnaireReport: (userId: string, policyId: string) => (dispatch: any) => {
		return policyAPI.fetchQuestionnaireReport(userId, policyId)
			.then((questionnaireReport: string) => {
				dispatch({ type: actionTypes.FETCH_QUESTIONNAIRE_REPORT_SUCCESS, questionnaireReport });
			})
			.catch(err => {
				throw err;
			});
	},
	fetchPolicyAnswer: (policyId: string) => (dispatch: any) => {
		return policyAPI.fetchPolicyAnswer(policyId)
			.then((answers: PolicyAnswerWithDeclineInfoDto[]) => {
				dispatch({ type: actionTypes.FETCH_POLICY_ANSWER_SUCCESS, answers});
			})
			.catch(err => {
				throw err;
			});
	},
	saveCustomerPolicy: (customerPolicy: CustomerPolicy) => (dispatch: any) => {
		return policyAPI.saveCustomerPolicy(customerPolicy)
			.then((savedCustomerPolicy: CustomerPolicy) => {
				dispatch({ type: actionTypes.SAVE_CUSTOMER_POLICY, customerPolicy: savedCustomerPolicy });
			})
			.catch(err => {
				throw err;
			});
	},
	updatePolicyPaperCopyStatus: (policyId: string) => (dispatch: any) => {
		return policyAPI.updatePolicyPaperCopyStatus(policyId)
			.then(() => {
				dispatch({ type: actionTypes.UPDATE_POLICY_PAPER_STATUS });
			})
			.catch(err => {
				throw err;
			});
	},
	saveCustomerPolicyBeneficiary: (customerPolicyBeneficiary: CustomerPolicyBeneficiaryDto) => (dispatch: any) => {
		return policyAPI.saveCustomerPolicyBeneficiary(customerPolicyBeneficiary)
			.then((savedBeneficiary: CustomerPolicyBeneficiaryDto) => {
				dispatch({ type: actionTypes.SAVE_CUSTOMER_POLICY_BENEFICIARY, customerPolicyBeneficiary: savedBeneficiary });
			})
			.catch(err => {
				throw err;
			});
	},
	saveCustomerPolicyCurrentBeneficiary: (customerPolicyCurrentBeneficiary: CustomerPolicyBeneficiaryDto) => (dispatch: any) => {
		return policyAPI.saveCustomerPolicyCurrentBeneficiary(customerPolicyCurrentBeneficiary)
			.then((savedBeneficiary: CustomerPolicyBeneficiaryDto) => {
				dispatch({ type: actionTypes.SAVE_CUSTOMER_POLICY_CURRENT_BENEFICIARY, customerPolicyBeneficiary: savedBeneficiary });
			})
			.catch(err => {
				throw err;
			});
	},
	approveAllBeneficiariesForCustomer: (request: ApproveAllBeneficiariesForNewCustomerRequest) => (dispatch: any) => {
		return policyAPI.approveAllBeneficiariesForCustomer(request)
			.then(() => {
				dispatch({ type: actionTypes.APPROVE_ALL_BENEFICIARIES });
			})
			.catch(err => {
				throw err;
			});
	},
	saveCustomerPolicyAnswer: (policyId: string, questionName: string, value: string) => (dispatch: any) => {
		return policyAPI.saveCustomerPolicyAnswer(policyId, questionName, value)
			.then((answer: any) => {
				dispatch({ type: actionTypes.SAVE_CUSTOMER_POLICY_ANSWER, answer });
			})
			.catch(err => {
				throw err;
			});
	},
	updatePolicy: (policyId: string) => (dispatch: any) => {
		return policyAPI.updatePolicy(policyId)
			.then((updatedCustomerPolicy: CustomerPolicy) => {
				dispatch({ type: actionTypes.UPDATE_CUSTOMER_POLICY, customerPolicy: updatedCustomerPolicy });
			})
			.catch(err => {
				throw err;
			});
	},
	deleteCustomerPolicy: (customer: UserDetails, customerPolicy: CustomerPolicy) => (dispatch: any) => {
		return policyAPI.deleteCustomerPolicy(customer.stripeCustomerId, customerPolicy.stripePlanId)
			.then((cancelledCustomerPolicy: CustomerPolicy) => {
				dispatch({ type: actionTypes.DELETE_CUSTOMER_POLICY, customerPolicy: cancelledCustomerPolicy });
			})
			.catch(err => {
				throw err;
			});
	},
	deleteCustomerPolicyBeneficiary: (beneficiaryId: string, statusId: string) => (dispatch: any) => {
		return policyAPI.deleteCustomerPolicyBeneficiary(beneficiaryId, statusId)
			.then((deleteSucceed: Boolean) => {
				dispatch({ type: actionTypes.DELETE_CUSTOMER_POLICY_BENEFICIARY, deleteSucceed });
			})
			.catch(err => {
				throw err;
			});
	},
	deleteBeneficiaryDuringSignUp: (beneficiaryId: string) => (dispatch: any) => {
		return policyAPI.deleteBeneficiaryDuringSignUp(beneficiaryId)
			.then(() => {
				dispatch({ type: actionTypes.DELETE_BENEFICIARY_DURING_SIGN_UP });
			})
			.catch(err => {
				throw err;
			});
	},
	rebuildCustomerPolicyPdf: (customer: UserDetails, customerPolicy: CustomerPolicy) => (dispatch: any) => {
		return policyAPI.rebuildCustomerPolicyPdf(customer.id!, customerPolicy.id!)
			.catch(err => {
				throw err;
			});
	},
	sendBeneficiaryNotification: (userId: string, beneficiaryId: string) => (dispatch: any) => {
		return policyAPI.sendBeneficiaryNotification(userId, beneficiaryId)
			.catch(err => {
				throw err;
			});
	},
	sendBeneficiaryUploadedConfirmation: (userId: string, policyId: string) => (dispatch: any) => {
		return policyAPI.sendBeneficiaryUploadedConfirmation(userId, policyId)
			.catch(err => {
				throw err;
			});
	},
	saveAdminCustomerPolicy: (customerPolicy: AdminUpdateCustomerPolicyRequest) => (dispatch: any) => {
		return policyAPI.saveAdminCustomerPolicy(customerPolicy)
			.then((savedCustomerPolicy: CustomerPolicy) => {
				dispatch({ type: actionTypes.SAVE_CUSTOMER_POLICY, customerPolicy: savedCustomerPolicy });
			})
			.catch(err => {
				throw err;
			});
	},
	sendCancelPolicyEmail: (userId: string, policyId: string) => (dispatch: any) => {
		return policyAPI.sendCancelPolicyEmail(userId, policyId)
			.catch(err => {
				throw err;
			});
	},
	setRedirectFromAdjudication: (redirect: boolean) => {
		return (dispatch: any) => {
			dispatch({ type: actionTypes.REDIRECT_FROM_ADJUDICATION, redirectFromAdjudication: redirect });
		};
	},
};

export default policyActions;


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/policy/policyActions.ts